import { useMemo, useSyncExternalStore } from 'react';
import { getStatsigStringListDynamicConfig } from '../bootstrap/StatSigInitialization';
import { STATSIG_DYNAMIC_CONFIGS } from '../bootstrap/statsigFeatureGates';
import { statsigStore } from '../bootstrap/statsigStore';
import {
	deriveIsRovoContextMenuEnabled,
	deriveIsRovoContextMenuHighlightFabEnabled,
	deriveIsRovoContextMenuHighlightMenuDirect,
	deriveIsRovoContextMenuHighlightOneLineMenuEnabled,
	deriveIsRovoContextMenuRightClickFabEnabled,
	deriveIsRovoContextMenuRightClickOneLineMenuEnabled,
	deriveIsRovoContextMenuSearchFullPageEnabled,
	deriveIsRovoContextMenuSearchInSidePanelEnabled,
	getAllRovoContextMenuVariants,
	type RovoContextMenuHighlightEntryVariant,
	type RovoContextMenuRightClickVariant,
	type RovoContextMenuSearchBehaviorVariant,
} from '../experiment/experiments';

export type ProactiveContextMenuVariants = {
	highlightEntry: RovoContextMenuHighlightEntryVariant;
	rightClickMenu: RovoContextMenuRightClickVariant;
	searchBehavior: RovoContextMenuSearchBehaviorVariant;
	isRovoContextMenuEnabled: boolean;
	isHighlightFabEnabled: boolean;
	isHighlightMenuDirect: boolean;
	isHighlightOneLineMenuEnabled: boolean;
	isRightClickFabEnabled: boolean;
	isRightClickOneLineMenuEnabled: boolean;
	isSearchInSidePanelEnabled: boolean;
	isSearchFullPageEnabled: boolean;
	/** Hosts the visible surfaces must not render on; absent until the config resolves. */
	blockedHosts?: string[];
};

/** Empty on purpose — the config owns the list, so edits never need a release. */
const DEFAULT_BLOCKED_HOSTS: string[] = [];

export const useProactiveContextMenuVariants = (): ProactiveContextMenuVariants => {
	const statsigVersion = useSyncExternalStore(
		statsigStore.subscribe,
		statsigStore.getSnapshot,
		statsigStore.getSnapshot,
	);

	return useMemo<ProactiveContextMenuVariants>(() => {
		const { highlightEntry, rightClickMenu, searchBehavior } = getAllRovoContextMenuVariants();
		return {
			highlightEntry,
			rightClickMenu,
			searchBehavior,
			isRovoContextMenuEnabled: deriveIsRovoContextMenuEnabled(
				highlightEntry,
				rightClickMenu,
				searchBehavior,
			),
			isHighlightFabEnabled: deriveIsRovoContextMenuHighlightFabEnabled(highlightEntry),
			isHighlightMenuDirect: deriveIsRovoContextMenuHighlightMenuDirect(highlightEntry),
			isHighlightOneLineMenuEnabled:
				deriveIsRovoContextMenuHighlightOneLineMenuEnabled(highlightEntry),
			isRightClickFabEnabled: deriveIsRovoContextMenuRightClickFabEnabled(rightClickMenu),
			isRightClickOneLineMenuEnabled:
				deriveIsRovoContextMenuRightClickOneLineMenuEnabled(rightClickMenu),
			isSearchInSidePanelEnabled: deriveIsRovoContextMenuSearchInSidePanelEnabled(searchBehavior),
			isSearchFullPageEnabled: deriveIsRovoContextMenuSearchFullPageEnabled(searchBehavior),
			// `initializeConfigClient` emits on `statsigStore`, so this re-reads once it settles.
			blockedHosts: getStatsigStringListDynamicConfig(
				STATSIG_DYNAMIC_CONFIGS.CONTEXT_MENU_BLOCKLIST,
				DEFAULT_BLOCKED_HOSTS,
			),
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [statsigVersion]);
};
